import PropTypes from 'prop-types';
import { labelValuePairProp, textProp } from '../../tools/customProps';

// Common prop types
export const textPairProp = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string,
});

export const TextPairType = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string,
});

export const ProductType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  sku: PropTypes.string,
  storeItem: PropTypes.string,
  price: PropTypes.string.isRequired,
  formattedPrice: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  productUrl: PropTypes.string,
  collection: PropTypes.string,
  colorSequence: PropTypes.string,
  isSample: PropTypes.bool.isRequired,
  isGiftBox: PropTypes.bool.isRequired,
  isFreeGift: PropTypes.bool.isRequired,
  isCharityItem: PropTypes.bool.isRequired,
  isFinalSaleItem: PropTypes.bool.isRequired,
  useKicUrl: PropTypes.bool.isRequired,
});

export const OrderProductTextType = PropTypes.shape({
  size: TextPairType,
  color: TextPairType,
  item: TextPairType,
  storeItemNumber: TextPairType,
  finalSaleMessage: TextPairType,
  sku: TextPairType,
  cancelItem: TextPairType,
  returnForRefund: TextPairType,
  returnThisItem: TextPairType,
  exchangeButtonLabel: TextPairType,
  reserveAgain: TextPairType,
  itemsInReservation: TextPairType,
});

export const OrderProductType = PropTypes.shape({
  product: ProductType.isRequired,
  departmentName: PropTypes.string,
  status: PropTypes.string,
  statusIcon: PropTypes.string,
  isCancellable: PropTypes.bool.isRequired,
  quantity: PropTypes.string.isRequired,
  totalPrice: PropTypes.number.isRequired,
  salesTax: PropTypes.number,
  formattedSalesTax: PropTypes.string,
  totalItemsPrice: PropTypes.number,
  totalItemsPriceWithoutTax: PropTypes.number,
  formattedTotalItemsPriceWithoutTax: PropTypes.string,
  itemPrice: PropTypes.number,
  formattedItemPrice: PropTypes.string,
  itemPricePaid: PropTypes.number,
  formattedItemPricePaid: PropTypes.string,
  shippingTax: PropTypes.number,
  formattedShippingTax: PropTypes.string,
  packageNumber: PropTypes.string,
  expectedDeliveryDate: PropTypes.string,
  text: OrderProductTextType,
});

// Order Details Transaction Summary
const TransactionRowProp = PropTypes.shape({
  label: textProp.isRequired,
  isNegative: PropTypes.bool,
  price: PropTypes.string,
});

export const TransactionRowType = PropTypes.shape({
  label: TextPairType,
  price: PropTypes.string,
  isNegative: PropTypes.bool,
  className: PropTypes.string,
});

export const transactionSummaryProps = PropTypes.shape({
  itemLabel: textProp.isRequired,
  isNegative: PropTypes.bool,
  priceLabel: textProp.isRequired,
  rows: PropTypes.arrayOf(TransactionRowProp).isRequired,
  footer: TransactionRowProp,
  number: PropTypes.string,
});

export const transactionSummaryDefaultProps = {
  isNegative: false,
  footer: null,
};

// Resend Order Confirmation Button State
export const resendOrderButtonProps = PropTypes.shape({
  buttonText: textProp,
  processing: textProp,
  error: textProp,
  success: textProp,
  number: PropTypes.string,
});

export const resendOrderButtonDefaultProps = {
  buttonText: null,
  processing: null,
  error: null,
  success: null,
  number: null,
};

export const cancelOrderControlProps = {
  email: PropTypes.string.isRequired,
  label: textProp.isRequired,
  orderNumber: PropTypes.string.isRequired,
};

// Order Details CTA Group Buttons
export const ctaGroupButtonsProps = {
  ctaGroup: PropTypes.arrayOf(labelValuePairProp),
  number: PropTypes.string,
  resendOrder: resendOrderButtonProps,
};

export const ctaGroupButtonsDefaultProps = {
  ctaGroup: [],
  resendOrder: null,
  number: null,
};

export const shippingAddress = {
  accessPointName: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  country: PropTypes.string,
  formattedCityStateZip: PropTypes.string,
  formattedName: PropTypes.string,
  mapUrl: labelValuePairProp,
};

export const shippingAddressCardProps = {
  shippingAddress: PropTypes.shape({
    title: textProp.isRequired,
    shippedToAddress: PropTypes.shape(shippingAddress).isRequired,
  }),
};

const billingInfoItem = PropTypes.shape({
  paymentIcon: PropTypes.string,
  paymentIconLabel: PropTypes.string,
  cardNumber: PropTypes.string,
  cardExpiration: PropTypes.string,
  paymentType: PropTypes.string,
  email: PropTypes.string,
  paymentURL: PropTypes.string,
  paymentStatus: PropTypes.string,
  billingAddress: PropTypes.shape(shippingAddress),
  konbiniLocationLabel: textProp,
});

export const billingInfoItemProps = {
  title: PropTypes.string,
  billingInfoItem,
};

export const billingInfoCardProps = {
  billingInfo: PropTypes.shape({
    title: textProp.isRequired,
    billingInfoList: PropTypes.arrayOf(billingInfoItem).isRequired,
  }),
};

export const productListProps = {
  orderProducts: PropTypes.arrayOf(OrderProductType).isRequired,
  text: PropTypes.shape({
    size: TextPairType,
    color: TextPairType,
    item: TextPairType,
    storeItemNumber: TextPairType,
    finalSaleMessage: TextPairType,
    sku: TextPairType,
    cancelItem: TextPairType,
    returnForRefund: TextPairType,
    returnThisItem: TextPairType,
    exchangeButtonLabel: TextPairType,
    reserveAgain: TextPairType,
    itemsInReservation: TextPairType,
  }),
};

export const PackageType = PropTypes.shape({
  packageNumber: PropTypes.string,
  trackingNumber: PropTypes.string,
  dateReceived: PropTypes.string,
  carrier: PropTypes.string,
  trackingURL: PropTypes.string,
  productPrice: PropTypes.string,
  formattedProductPrice: PropTypes.string,
  shippingCharge: PropTypes.number,
  formattedShippingCharge: PropTypes.string,
  tax: PropTypes.number,
  formattedTax: PropTypes.string,
  additionalCharges: PropTypes.number,
  dateShipped: PropTypes.string,
  status: PropTypes.string,
  adjustments: PropTypes.number,
  formattedAdjustments: PropTypes.string,
  packageTotalPrice: PropTypes.number,
  formattedPackageTotalPrice: PropTypes.string,
  orderLineKeys: PropTypes.arrayOf(PropTypes.string),
});

export const packageGroupProps = {
  packageData: PropTypes.shape({
    ...PackageType.shape,
    packageSummaryData: transactionSummaryProps,
  }),
  allOrderProducts: PropTypes.arrayOf(PropTypes.shape({
    orderLineKey: PropTypes.string,
  })),
  text: PropTypes.shape({
    packageHeaderText: PropTypes.string,
    trackPackageText: PropTypes.string,
  }),
};

const extendedVatDetails = PropTypes.shape({
  vatIncludedInMerchPriceToCountry: textProp,
  vatDeductedNonCountryShipments: textProp,
  recipientNotLiableForImportDuties: textProp,
  vatRefundedForMerhandiseReturns: textProp,
});

export const vatDutyDetailsProps = {
  vatDutyDetails: PropTypes.shape({
    title: textProp,
    recipientNotChargedAddlDuties: textProp,
    extendedVatDetails,
    sandhVatNotRefundedMsg: textProp,
  }),
};

export const orderSummaryProps = {
  shippingDetails: shippingAddressCardProps.shippingAddress,
  billingInfo: billingInfoCardProps.billingInfo,
  vatDutyDetails: vatDutyDetailsProps.vatDutyDetails,
};

export const orderSummaryDefaultProps = {
  shippingDetails: null,
  billingInfo: null,
  vatDutyDetails: null,
};

export const orderItemsContainerProps = {
  orderProducts: PropTypes.arrayOf(OrderProductType).isRequired,
  packages: PropTypes.arrayOf(PackageType),
  text: PropTypes.shape({
    packageHeaderText: PropTypes.string,
    trackPackageText: PropTypes.string,
    size: TextPairType,
    color: TextPairType,
    item: TextPairType,
    storeItemNumber: TextPairType,
    finalSaleMessage: TextPairType,
    sku: TextPairType,
    cancelItem: TextPairType,
    returnForRefund: TextPairType,
    returnThisItem: TextPairType,
    exchangeButtonLabel: TextPairType,
    reserveAgain: TextPairType,
    itemsInReservation: TextPairType,
  }),
};

export const orderItemsContainerDefaultProps = {
  packages: null,
  text: {},
};

export const orderDetailsDataProps = {
  queryData: PropTypes.object,
};

export const orderDetailsDataDefaultProps = {
  queryData: {},
};

export const rneOrderDetailsDataProps = {
  rneQueryData: PropTypes.object,
};

export const rneOrderDetailsDataDefaultProps = {
  rneQueryData: {},
};
